import { useMemo, useState } from "react"
import clsx from "clsx"

import { Avatar, TypeIcon } from "@/components/Standup"
import type { Releases as ReleasesData, ReleaseItem, StateCategory } from "@/types/sprint-report"

interface Props {
  data: ReleasesData
}

type Phase = "rc" | "ga"
const PHASE_PREFIX: Record<Phase, string> = { rc: "Release-", ga: "GA-" }

const PRIORITY_BADGE: Record<string, string> = {
  P0: "bg-priority-p0 text-white",
  P1: "bg-priority-p1 text-white",
  P2: "bg-priority-p2 text-white",
  P3: "bg-priority-p3 text-white",
}

/** State category → pill tone. Done reads green; everything open stays neutral
 *  blue so the eye lands on what's shipped vs still moving. */
const CAT_TONE: Record<StateCategory, string> = {
  done: "bg-win-light text-win-deep ring-win/30",
  in_qa: "bg-blue-50 text-blue-700 ring-blue-200",
  dev_complete: "bg-indigo-50 text-indigo-700 ring-indigo-200",
  in_dev: "bg-amber-50 text-amber-800 ring-amber-200",
  pre_dev: "bg-slate-100 text-slate-600 ring-slate-200",
  removed: "bg-slate-100 text-slate-500 ring-slate-200",
  other: "bg-slate-100 text-slate-600 ring-slate-200",
}

function StatePill({ state, category }: { state: string; category: StateCategory }) {
  return (
    <span
      className={clsx(
        "inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ring-1",
        CAT_TONE[category] ?? CAT_TONE.other,
      )}
    >
      {state}
    </span>
  )
}

function Row({ it }: { it: ReleaseItem }) {
  return (
    <tr className="align-top">
      <td className="num !text-right text-slate-400">
        <a href={it.url} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">{it.id}</a>
      </td>
      <td><span className="inline-flex items-center gap-1.5 whitespace-nowrap text-slate-600"><TypeIcon type={it.work_item_type} /> {it.work_item_type}</span></td>
      <td>
        {it.priority ? (
          <span className={clsx("rounded px-1.5 py-0.5 text-[10px] font-bold leading-none", PRIORITY_BADGE[it.priority.major] ?? "bg-slate-200 text-slate-700")}>
            {it.priority.raw || it.priority.major}
          </span>
        ) : (
          <span className="text-slate-300">—</span>
        )}
      </td>
      <td><StatePill state={it.state} category={it.state_category} /></td>
      <td className="min-w-[280px]">
        <a href={it.url} target="_blank" rel="noreferrer" className="font-medium text-ink hover:underline">{it.title}</a>
      </td>
      <td>
        {it.assigned_to ? (
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-[11px] text-ink">
            <Avatar name={it.assigned_to.name} /> {it.assigned_to.name}
          </span>
        ) : (
          <span className="text-slate-300">Unassigned</span>
        )}
      </td>
    </tr>
  )
}

/** A labelled list (Done or Not done) for one group of release items. */
function Group({ title, done, items }: { title: string; done: boolean; items: ReleaseItem[] }) {
  if (items.length === 0) return null
  return (
    <section>
      <h3 className="mb-1.5 flex items-center gap-2">
        <span className={clsx("text-sm font-bold", done ? "text-win-deep" : "text-ink")}>{title}</span>
        <span
          className={clsx(
            "num rounded-full px-2 py-0.5 text-[11px] font-bold ring-1",
            done ? "bg-win-light text-win-deep ring-win/30" : "bg-slate-100 text-slate-600 ring-slate-200",
          )}
        >
          {items.length}
        </span>
      </h3>
      <div className="overflow-x-auto rounded-xl border border-slate-200/80">
        <table className="data-table">
          <thead>
            <tr>
              <th className="!text-right">#</th>
              <th>Type</th>
              <th>Pri</th>
              <th>Status</th>
              <th>Title</th>
              <th>Assignee</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => <Row key={it.id} it={it} />)}
          </tbody>
        </table>
      </div>
    </section>
  )
}

export function Releases({ data }: Props) {
  const [phase, setPhase] = useState<Phase>("rc")
  const [version, setVersion] = useState<string>(() => data.versions[0] ?? "")

  const tag = version ? `${PHASE_PREFIX[phase]}${version}` : ""
  const items = useMemo(
    () =>
      data.items
        .filter((it) => it.tags.includes(tag))
        .sort(
          (a, b) =>
            a.state_order - b.state_order ||
            (a.priority?.major ?? "P9").localeCompare(b.priority?.major ?? "P9") ||
            a.id - b.id,
        ),
    [data.items, tag],
  )

  const doneItems = items.filter((i) => i.state_category === "done")
  const openItems = items.filter((i) => i.state_category !== "done")
  const done = doneItems.length
  const byType = useMemo(() => {
    const m = new Map<string, number>()
    for (const it of items) m.set(it.work_item_type, (m.get(it.work_item_type) ?? 0) + 1)
    return [...m.entries()].sort((a, b) => b[1] - a[1])
  }, [items])

  if (data.versions.length === 0) {
    return (
      <p className="p-6 text-center text-sm text-slate-500">
        No release tags found yet. Tag work items with <code>Release-&lt;version&gt;</code> (RC) or{" "}
        <code>GA-&lt;version&gt;</code> (GA) in Azure DevOps and they'll appear here.
      </p>
    )
  }

  return (
    <div className="space-y-5">
      {/* Header + controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-bold text-ink">Releases</h2>
          <p className="text-xs text-slate-500">
            Work items tagged <code className="num">{tag || "—"}</code> in Azure DevOps &mdash; every
            type and priority in the {phase === "rc" ? "release candidate" : "GA release"}.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-lg bg-slate-100 p-0.5 text-xs font-semibold ring-1 ring-slate-200">
            {(["rc", "ga"] as Phase[]).map((p) => (
              <button
                key={p}
                onClick={() => setPhase(p)}
                aria-pressed={phase === p}
                className={clsx(
                  "rounded-md px-3 py-1 transition",
                  phase === p ? "bg-white text-header shadow-sm ring-1 ring-slate-200" : "text-slate-500 hover:text-ink",
                )}
              >
                {p.toUpperCase()}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-1.5 text-xs text-slate-500">
            Version
            <select
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm font-semibold text-ink outline-none focus:border-headerSub"
            >
              {data.versions.map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {/* Summary */}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="rounded-lg bg-white/70 px-3 py-1 ring-1 ring-slate-200">
          <span className="num text-base font-bold text-ink">{items.length}</span>{" "}
          <span className="text-slate-500">items</span>
        </span>
        <span className="rounded-lg bg-win-light/60 px-3 py-1 ring-1 ring-win/25">
          <span className="num text-base font-bold text-win-deep">{done}</span>{" "}
          <span className="text-win-deep/70">done</span>
        </span>
        <span className="rounded-lg bg-white/70 px-3 py-1 ring-1 ring-slate-200">
          <span className="num text-base font-bold text-ink">{items.length - done}</span>{" "}
          <span className="text-slate-500">remaining</span>
        </span>
        {byType.map(([t, n]) => (
          <span key={t} className="inline-flex items-center gap-1 rounded-lg bg-white/70 px-2.5 py-1 text-slate-600 ring-1 ring-slate-200">
            <TypeIcon type={t} /> {t} <span className="num font-semibold text-ink">{n}</span>
          </span>
        ))}
      </div>

      {/* Items — Done first, then Not done, as separate lists */}
      {items.length === 0 ? (
        <p className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
          No items tagged <code className="num">{tag}</code>{" "}
          {phase === "ga" && "yet — GA items appear once tagged at go-live."}
        </p>
      ) : (
        <div className="space-y-5">
          <Group title="Done" done items={doneItems} />
          <Group title="Not done" done={false} items={openItems} />
        </div>
      )}
    </div>
  )
}
